/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PersonalAssistantTab.tsx
 🗓️ Created: 2026-01-04
 🧩 Description:
    Personal Assistant tab for Extension form - Configure mini IVR
    with welcome message, digit options, and fallback actions.
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { Bot, Plus, Trash2, Phone } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import SelectModule from '../../../components/ui/SelectModule';
import SelectDestination from '../../../components/ui/SelectDestination';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import { useI18n } from '../../../hooks/useI18n';
import { v4 as uuidv4 } from 'uuid';
import type { PersonalAssistantState, PersonalAssistantOption } from '../types';

interface PersonalAssistantTabProps {
    personalAssistantData: PersonalAssistantState;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onPersonalAssistantChange: (field: string, value: any) => void;
}

const AVAILABLE_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'];

export function PersonalAssistantTab({
    personalAssistantData,
    onPersonalAssistantChange,
}: PersonalAssistantTabProps) {
    const { t } = useI18n();

    // State for fallback module (separate from destination)
    const [fallbackModule, setFallbackModule] = useState<string>(() => {
        // Parse initial fallback module from stored value
        const dest = personalAssistantData.fallbackAction;
        if (!dest) return '';
        const colonIndex = dest.indexOf(':');
        return colonIndex > 0 ? dest.substring(0, colonIndex) : '';
    });

    // Get used digits
    const usedDigits = personalAssistantData.options.map(opt => opt.digit);
    const availableDigitsForNew = AVAILABLE_DIGITS.filter(d => !usedDigits.includes(d));

    // Handlers
    const handleAddOption = () => {
        if (availableDigitsForNew.length === 0) return;

        const newOption: PersonalAssistantOption = {
            id: uuidv4(),
            digit: availableDigitsForNew[0],
            action: '', // Module type
            destination: '', // Just the destination value (e.g., "1001")
            timeout: 30,
        };
        onPersonalAssistantChange('options', [...personalAssistantData.options, newOption]);
    };

    const handleRemoveOption = (id: string) => {
        onPersonalAssistantChange('options', personalAssistantData.options.filter(opt => opt.id !== id));
    };

    const handleUpdateOption = (id: string, updates: Partial<PersonalAssistantOption>) => {
        const updatedOptions = personalAssistantData.options.map(opt =>
            opt.id === id ? { ...opt, ...updates } : opt
        );
        onPersonalAssistantChange('options', updatedOptions);
    };

    // Parse fallback destination value (just the value part, not the module prefix)
    const getFallbackDestinationValue = () => {
        const dest = personalAssistantData.fallbackAction;
        if (!dest) return '';
        const colonIndex = dest.indexOf(':');
        return colonIndex > 0 ? dest.substring(colonIndex + 1) : dest;
    };

    return (
        <div className="space-y-4">
            {/* Enable Personal Assistant */}
            <FormBox
                title={t('extensions.personalAssistant.title', 'Personal Assistant')}
                icon={<Bot className="h-5 w-5" />}
            >
                <div className="space-y-4">
                    {/* Row 1: Enable Personal Assistant + Instruction Message (only when enabled) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 items-start">
                        <SimpleTooltip
                            content={t('extensions.personalAssistant.enabled.tooltip', 'Enable the Personal Assistant mini IVR for this extension.')}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label className="text-left font-medium">
                                {t('extensions.personalAssistant.enabled.label', 'Enable Personal Assistant')}
                            </Label>
                        </SimpleTooltip>
                        <Toggle
                            value={personalAssistantData.enabled}
                            onChange={(value) => onPersonalAssistantChange('enabled', value)}
                        />
                        {personalAssistantData.enabled ? (
                            <>
                                <Label className="text-left font-medium">
                                    {t('extensions.personalAssistant.instructionMessage.label', 'Instruction Message')}
                                    <span className="text-destructive ml-0.5">*</span>
                                </Label>
                                <RecordingSelect
                                    value={personalAssistantData.instructionMessageRecordingId != null ? String(personalAssistantData.instructionMessageRecordingId) : undefined}
                                    onChange={(val) => onPersonalAssistantChange('instructionMessageRecordingId', val ?? null)}
                                    returnType="id"
                                    showPlayButton={true}
                                    placeholder={t('extensions.personalAssistant.instructionMessage.placeholder', 'Select a recording...')}
                                />
                            </>
                        ) : (
                            <div className="col-span-2"></div>
                        )}
                    </div>

                    {personalAssistantData.enabled && (
                        <>
                            {/* Row 2: Input Timeout + Max Retries */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 items-start">
                                <SimpleTooltip
                                    content={t('extensions.personalAssistant.timeout.tooltip', 'Time to wait for user input before executing fallback action.')}
                                    position="top"
                                    maxWidth="300px"
                                >
                                    <Label className="text-left font-medium">
                                        {t('extensions.personalAssistant.timeout.label', 'Input Timeout')}
                                    </Label>
                                </SimpleTooltip>
                                <select
                                    value={personalAssistantData.timeout}
                                    onChange={(e) => onPersonalAssistantChange('timeout', parseInt(e.target.value))}
                                    className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                >
                                    {[3, 5, 7, 10, 15, 20, 30].map(sec => (
                                        <option key={sec} value={sec}>{sec} {t('common.seconds', 'seconds')}</option>
                                    ))}
                                </select>
                                <SimpleTooltip
                                    content={t('extensions.personalAssistant.maxRetries.tooltip', 'Number of times to replay the menu if no valid input is received.')}
                                    position="top"
                                    maxWidth="300px"
                                >
                                    <Label className="text-left font-medium">
                                        {t('extensions.personalAssistant.maxRetries.label', 'Max Retries')}
                                    </Label>
                                </SimpleTooltip>
                                <select
                                    value={personalAssistantData.maxRetries}
                                    onChange={(e) => onPersonalAssistantChange('maxRetries', parseInt(e.target.value))}
                                    className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                >
                                    {[0, 1, 2, 3, 5].map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Row 3: If No Input */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 items-start">
                                <SimpleTooltip
                                    content={t('extensions.personalAssistant.fallback.actionTooltip', 'Action to take if no valid input is received after retries.')}
                                    position="top"
                                    maxWidth="300px"
                                >
                                    <Label className="text-left font-medium">
                                        {t('extensions.personalAssistant.fallback.action', 'If No Input')}
                                    </Label>
                                </SimpleTooltip>
                                <div className="col-span-3 flex gap-2">
                                    <div className="flex-1">
                                        <SelectModule
                                            value={fallbackModule}
                                            onChange={(module) => {
                                                setFallbackModule(module);
                                                onPersonalAssistantChange('fallbackAction', '');
                                            }}
                                            placeholder={t('extensions.personalAssistant.options.selectModule', 'Select Module')}
                                        />
                                    </div>
                                    <div className="flex-[2]">
                                        <SelectDestination
                                            moduleType={fallbackModule}
                                            value={getFallbackDestinationValue()}
                                            onChange={(value) => {
                                                onPersonalAssistantChange('fallbackAction', fallbackModule ? `${fallbackModule}:${value}` : value);
                                            }}
                                            placeholder={t('extensions.personalAssistant.options.selectDestination', 'Select Destination')}
                                            disabled={!fallbackModule}
                                        />
                                    </div>
                                </div>
                            </div>



                            {/* Digit Options Section */}
                            <div className="border border-border rounded-md p-4 bg-muted/20">
                                {personalAssistantData.options.length === 0 ? (
                                    <div className="text-center py-6 text-muted-foreground border border-dashed border-border rounded-md">
                                        <p className="text-sm">
                                            {t('extensions.personalAssistant.options.empty', 'No options configured. Add a digit option to get started.')}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {/* Table Header */}
                                        <div className="grid grid-cols-12 gap-2 px-2 py-1 bg-muted/50 rounded-md text-sm font-medium">
                                            <div className="col-span-1">{t('extensions.personalAssistant.options.digit', 'Digit')}</div>
                                            <div className="col-span-8">{t('extensions.personalAssistant.options.destination', 'Destination')}</div>
                                            <div className="col-span-2">{t('extensions.personalAssistant.options.timeout', 'Timeout')}</div>
                                            <div className="col-span-1"></div>
                                        </div>

                                        {/* Options Rows */}
                                        {personalAssistantData.options.map((option) => (
                                            <div key={option.id} className="grid grid-cols-12 gap-2 items-center p-2 border border-border rounded-md bg-background">
                                                <div className="col-span-1">
                                                    <select
                                                        value={option.digit}
                                                        onChange={(e) => handleUpdateOption(option.id, { digit: e.target.value })}
                                                        className="w-full px-2 py-1.5 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                                    >
                                                        <option value={option.digit}>{option.digit}</option>
                                                        {AVAILABLE_DIGITS.filter(d => d !== option.digit && !usedDigits.includes(d)).map(digit => (
                                                            <option key={digit} value={digit}>{digit}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-span-8 flex gap-2">
                                                    {/* Module Selector */}
                                                    <div className="flex-1">
                                                        <SelectModule
                                                            value={option.action}
                                                            onChange={(module) => {
                                                                // When module changes, clear destination
                                                                handleUpdateOption(option.id, { action: module, destination: '' });
                                                            }}
                                                            placeholder={t('extensions.personalAssistant.options.selectModule', 'Select Module')}
                                                        />
                                                    </div>
                                                    {/* Destination Selector */}
                                                    <div className="flex-[2]">
                                                        <SelectDestination
                                                            moduleType={option.action}
                                                            value={option.destination}
                                                            onChange={(value) => {
                                                                handleUpdateOption(option.id, { destination: value });
                                                            }}
                                                            placeholder={t('extensions.personalAssistant.options.selectDestination', 'Select Destination')}
                                                            disabled={!option.action}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-span-2">
                                                    <select
                                                        value={option.timeout}
                                                        onChange={(e) => handleUpdateOption(option.id, { timeout: parseInt(e.target.value) })}
                                                        className="w-full px-2 py-1.5 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                                    >
                                                        {[5, 10, 15, 20, 25, 30, 45, 60, 90, 120].map(sec => (
                                                            <option key={sec} value={sec}>{sec}s</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-span-1 flex justify-center">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleRemoveOption(option.id)}
                                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Add Button - outside the box, aligned right */}
                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddOption}
                                    disabled={availableDigitsForNew.length === 0}
                                    className="flex items-center gap-1"
                                >
                                    <Plus className="h-4 w-4" />
                                    {t('extensions.personalAssistant.options.addShort', 'Add')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </FormBox>
        </div>
    );
}
